import { css } from 'styled-components';

import { GUIDE_CARD_DESKTOP_WIDTH_PX, GUIDE_CARD_ROW_WIDTH_CSS } from '../guide/guideCardWidth';
import {
  PAGE_GUTTER_DESKTOP,
  PAGE_GUTTER_DESKTOP_NEGATIVE,
  trackCardWidthCss,
  trackCardWidthCssCapped,
} from '../lib/pageGutter';

/**
 * The hub's two shared layout decisions — the page gutter and the section
 * heading register — in one place, because the board's revision pass (THE-577,
 * 2026-09-25) was mostly about them.
 *
 * ONE HEADING SIZE. The board asked for "That's so them" to be the same size as
 * the Occasions title and "Find a gift they'll love". Those three were three
 * different sizes: the page <h1> at 26/28, the grid heading at 19/21, and the
 * occasions rail's at 28 flat. Rather than retune three components to agree by
 * coincidence, every hub section heading now reads THIS, so the next reword
 * cannot reintroduce the mismatch.
 *
 * "Trending now" is included even though the board named only three titles: it
 * is the one remaining section heading, and leaving it at 19px beside four
 * 26px siblings reads as a rendering bug rather than a hierarchy. Called out in
 * the revision comment so it can be reverted on its own if that was wrong.
 *
 * The gutter is lifted verbatim from what four hub components had each spelled
 * out separately. `box-sizing` is load-bearing in STORYBOOK, which does not
 * load bootstrap.css and therefore does not get the reboot's `* { box-sizing }`
 * — without it a `width: 100%` wrapper computes 32px wider than the viewport
 * and every story becomes an unfaithful picture of the page.
 */

/**
 * The page gutter. 20px on mobile rather than 16 — the board asked for more
 * breathing room on the left on 2026-09-26, and raising it HERE rather than on
 * the two carousels keeps the trending cards, the occasion circles, the section
 * headings and the browse grid on one line. Indenting only the scrollers would
 * have bought the extra 4px at the cost of the alignment.
 *
 * NO MAX-WIDTH, NO MARGIN:AUTO (THE-1161) — ONLY WHEN `$wideGutter` IS SET.
 * Those two used to cap this box at 1400px and center it, which is exactly
 * what left a band of the page's own background colour showing beside the
 * hub's sticky header (164px of it at 1728px wide) and stopped every
 * carousel short of the real screen edge: a scrolling track escapes ITS OWN
 * parent's padding with a negative margin (see HUB_TRACK_GUTTER_CSS below),
 * but it cannot escape a parent that is independently capped at 1400px and
 * floating in the middle of a wider viewport. Padding alone, with the
 * >=1024px side growing via `PAGE_GUTTER_DESKTOP`, reaches the real edge at
 * every width AND still tops out at a 1440px content measure on anything
 * wider — the cap bought nothing a growing gutter doesn't already give for
 * free.
 *
 * `$wideGutter` (Kate, 2026-10-28, PR #883: rides the Gift Guide hub gate,
 * no preview switch of its own — see `giftGuideHubFlag.ts`) picks
 * between that description (true) and the pre-PR cap-and-flat-64px box
 * (false/omitted, every consumer's default) at >=1024px only — below that,
 * both branches are identical, matching the ticket's mobile-byte-identical
 * acceptance bar. Every caller of this mixin must declare `$wideGutter?:
 * boolean` on its own styled-component for the prop to reach here —
 * styled-components forwards the WHOLE component's props to every
 * interpolated function in its template, regardless of which mixin a given
 * interpolation came from.
 *
 * NO NESTED `css` TAG HERE, deliberately: this fragment is itself spliced
 * into ANOTHER component's own backtick-delimited template (see each
 * caller), and more than one source-text fitness test (HubCarouselPeekGeometry.
 * test.ts) extracts a styled-component's CSS by scanning for its first
 * closing backtick — a nested `css\`...\`` here would terminate that scan
 * early and silently truncate what the test actually reads. Plain-string
 * interpolations below sidestep that entirely.
 */
export const HUB_GUTTER_CSS = css<{ $wideGutter?: boolean }>`
  box-sizing: border-box;
  ${({ $wideGutter }) => !$wideGutter && 'max-width: 1400px; margin: 0 auto;'}
  width: 100%;
  padding-left: 20px;
  padding-right: 20px;
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
  }
  @media (min-width: 1024px) {
    padding-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
    padding-right: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
  }
`;

/**
 * The gutter for a HORIZONTALLY SCROLLING track, which is NOT the same CSS as
 * the page gutter even though it produces the same inset.
 *
 * `scroll-padding-left` is the load-bearing line, and leaving it out is why the
 * board saw the trending row's first card "way up against the edge" while the
 * component said `padding-left: 16px`. A snap container with
 * `scroll-snap-align: start` children aligns the first child to the SNAPPORT's
 * start edge, and the snapport is inset by `scroll-padding`, which defaults to
 * zero — not by `padding`. So the browser silently scrolls the track right by
 * exactly the padding amount on first paint and the padding disappears.
 *
 * Measured on the live page at 390px before touching anything: `padding-left:
 * 16px` → `scrollLeft: 16` → first card at x=0. The naive fix is measurably
 * useless — forcing `padding-left: 32px` gave `scrollLeft: 32` and the first
 * card still at x=0. Adding `scroll-padding-left` gave `scrollLeft: 0` and the
 * card at x=24, which is the only variant that moved the pixel the board was
 * pointing at.
 *
 * It is one mixin rather than two copies because BOTH hub scrollers had the bug
 * (trending row and occasion circles), from the same omission, and the next
 * scroller someone adds would have had it too. Keep the two values equal: they
 * are the same inset expressed to the layout and to the scroll-snap engine.
 */
export const HUB_TRACK_GUTTER_CSS = css<{ $wideGutter?: boolean }>`
  padding-left: 20px;
  padding-right: 20px;
  scroll-padding-left: 20px;
  @media (min-width: 640px) {
    padding-left: 32px;
    padding-right: 32px;
    scroll-padding-left: 32px;
  }
  @media (min-width: 1024px) {
    padding-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
    padding-right: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
    scroll-padding-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP : '64px')};
  }
`;

/**
 * THE-1161: the negative-margin counterpart to HUB_GUTTER_CSS, for a
 * scrolling track that bleeds out of a HUB_GUTTER_CSS-padded parent (e.g.
 * HubBandContent) back to the real screen edge. Replaces each band's own
 * hardcoded `-20px`/`-32px`/`-64px` margins — those matched HUB_GUTTER_CSS's
 * OLD flat 64px exactly and silently stopped cancelling it once the parent's
 * own padding started growing past 64px beyond a 1568px-wide viewport.
 *
 * `$wideGutter` (same gate as HUB_GUTTER_CSS, see that doc comment): below
 * 1024px both branches are the same flat `-20px`/`-32px` every caller had
 * pre-PR; at >=1024px, `false`/omitted reproduces every caller's OLD flat
 * `-64px` (the exact value HubFindsBand/HubPicksBand/HubOccasionTiles each
 * hardcoded before this mixin existed) rather than the new, viewport-growing
 * negative gutter.
 */
export const HUB_TRACK_BLEED_CSS = css<{ $wideGutter?: boolean }>`
  margin-left: -20px;
  margin-right: -20px;
  @media (min-width: 640px) {
    margin-left: -32px;
    margin-right: -32px;
  }
  @media (min-width: 1024px) {
    margin-left: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP_NEGATIVE : '-64px')};
    margin-right: ${({ $wideGutter }) => ($wideGutter ? PAGE_GUTTER_DESKTOP_NEGATIVE : '-64px')};
  }
`;

/**
 * HubOccasionTiles' >=1024px column width, hoisted here rather than left
 * inline so HubSearchHeader's sticky search field can derive its own trim
 * from the SAME tile measure instead of carrying a second, independently-
 * drifting copy of it (THE-860's original bug: the trim was a bare literal
 * that only ever matched one snapshot of the tile width and silently
 * stopped matching the next time the tile width changed).
 *
 * THE-1161 round 8 (board, 2026-10-27): re-fit to the same trackCardWidthCss
 * shape as the other four rows, with its own ceiling — the one row the
 * board asked to also stop growing, not merely re-fit ("coming down from
 * 354 to about 300 max"). Gap moves from this row's old standalone 10px to
 * the one shared hub-row gap (24px) — mobile/tablet (<1024px) keep their own
 * 10px, declared inline on HubOccasionTiles' Track, untouched.
 */
export const HUB_OCCASION_TILE_DESKTOP_WIDTH = trackCardWidthCssCapped(160, 5.3, 24, 296);
export const HUB_OCCASION_TILE_GAP_PX = 24;

/**
 * THE-1161, Kate's 2026-10-26 peek decision: "every row shows about a third
 * of the next card at every desktop width" — like Amazon's home rows, not the
 * "just avoid a zero-peek collision" bar HubFindsBand/HubPicksBand's Cell
 * (round 4) cleared. Shepherd QA measured "New guides" and "Guides picked for
 * your people" (both `GUIDE_CARD_ROW_WIDTH_CSS`'s flat 384px) swinging from a
 * 4px sliver at 1280 to a 372px peek at 1728 — proof a flat width's peek
 * fraction is accidental, not designed, exactly like Finds/Picks before round
 * 4 fixed it there.
 *
 * SAME TECHNIQUE, NARROWER SCOPE. `GUIDE_CARD_ROW_WIDTH_CSS` is not owned by
 * the hub — the homepage/People "Trending gift guides" rail, the recipient
 * board's "Gift guides" rail, the search page's Guides row and the
 * collection tray all size off it too (see that module's own doc comment),
 * and Kate's question named only the hub's five rows plus the People page's
 * Coming up strip. Widening the shared constant's desktop width would move
 * four surfaces nobody asked about; layering this on top instead reaches
 * `HubNewGuidesRow.tsx` and `HubPersonGuidesRow.tsx`.
 *
 * SEARCH IS NOT ONE OF THE "FOUR SURFACES NOBODY ASKED ABOUT" THOUGH IT
 * LOOKS LIKE IT SHOULD BE (shepherd re-QA round 5 correction — an earlier
 * revision of this comment claimed it was untouched). The search page has no
 * row component of its own: `HubSearchView.tsx` renders `HubNewGuidesRow.tsx`
 * directly (`guideCardRowWidth.fitness.test.ts` pins that delegation), so it
 * picks up this clamp as a consequence of sharing that component, not
 * because Kate's question named it. That is "any other hub row" rendering
 * the row it always has, not a scope miss — nothing here singles search out.
 *
 * THE NUMBERS (round 8, board 2026-10-27): `trackCardWidthCss(230, 3.3, 24)`
 * — see pageGutter.ts's doc comment for why this divides the track's own
 * visible width rather than scaling a flat `vw` rate, which is what let this
 * row swing from a 4px peek at 1280 to within single digits of a whole extra
 * card at 1920 under the old clamp(). Both rows now share the one gap (24px,
 * was 20px "New guides"/search past 640px, 24px "Guides picked for your
 * people" past 768px) — mobile/tablet keep each row's own existing gap,
 * untouched. 32-33% across 1024/1100/1180/1280/1366/1440/1512/1728/1920 (see
 * HubCarouselPeekGeometry.test.ts), independent of how many guides are in
 * the row. A row whose cards all fit without scrolling (Kate's own example:
 * "Guides picked for your people" with 3 cards) renders the same geometry
 * and simply never reaches the part of it that would show a peek — nothing
 * here forces an artificial overflow.
 *
 * `$wideGutter` (same gate as HUB_GUTTER_CSS, the Gift Guide hub gate): pre-PR,
 * neither `HubNewGuidesRow` nor `HubPersonGuidesRow` had ANY >=1024px
 * override at all — both rode `GUIDE_CARD_ROW_WIDTH_CSS`'s own flat 384px
 * straight through from its 640px breakpoint. `false`/omitted reasserts
 * that same 384px/none pair (redundant with the 640px step, but harmless —
 * no nested `css` tag here, see HUB_GUTTER_CSS's note on why), so the clamp
 * below only takes visible effect once a caller opts in.
 */
export const HUB_GUIDE_CARD_ROW_WIDTH_CSS = css<{ $wideGutter?: boolean }>`
  ${GUIDE_CARD_ROW_WIDTH_CSS}
  @media (min-width: 1024px) {
    width: ${({ $wideGutter }) =>
      $wideGutter ? trackCardWidthCss(230, 3.3, 24) : GUIDE_CARD_DESKTOP_WIDTH_PX + 'px'};
    max-width: none;
  }
`;

export const HUB_HEADING_CSS = css`
  text-align: left;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.2;
  margin: 0;
  @media (min-width: 768px) {
    font-size: 28px;
  }
`;

/**
 * The hub's header rule: DESKTOP ONLY, for the cohort (board, THE-577,
 * 2026-09-26: "header on desktop only — phones stay clean, desktop keeps its
 * nav"), refining their earlier "remove the Thea header".
 *
 * 1024px IS NOT AN ARBITRARY DESKTOP GUESS — it is the exact width at which
 * `SiteHeader`'s nav links and account avatar stop being `display: none`.
 * Below it the header is a bare wordmark carrying no navigation at all, so
 * hiding it there costs the cohort nothing; above it, the desktop tester gets
 * their way off the page back.
 *
 * CSS, NOT A WIDTH HOOK. A JS breakpoint makes the header's presence depend on
 * render-time window width, so a resize across 1024 needs a re-render and the
 * first paint can disagree with the viewport. A media query cannot be wrong.
 *
 * Shared from here (THE-633) rather than spelled out per page, because the hub
 * index and the topic ROUTE are two pages the same tester walks between in one
 * tap — a breakpoint that drifts between them shows up as a header that
 * appears halfway through a journey.
 */
export const HUB_DESKTOP_ONLY_HEADER_CSS = css`
  display: none;
  @media (min-width: 1024px) {
    display: block;
  }
`;
